import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../../Auth/AuthContext";
import { Alert, Dialog, DialogTitle, DialogContent, Typography, TextField, InputLabel, Button } from "@mui/material";
import { useTickets } from "../../../hooks/useTickets";
import { closePdfPopup, openPdfBlob, openPdfPopup, updateMovimientoMonto } from "../../../utils/api";
import { authenticatedSessionRequestConfig } from "../../../utils/csrf";
import { formatApiMoneyForInput, normalizeApiMoney, parseColombianMoneyInput } from "../../../utils/money";
import {
  appendCreatedTicket,
  buildTicketCreationContext,
  createTicketSnapshot,
  getTicketCreationErrorMessage,
  runTicketCreationOnce,
} from "../../../utils/ticketCreation";

const API_URL = process.env.REACT_APP_API_URL;

const TicketSalidaForm = ({openTicketSalidaForm, setOpenTicketSalidaForm, data, areUserAdmin, handleUpdateData, setValorModificableParaVer, valorModificableParaVer}) => {
   const ticketsEgresos = useTickets('salida', data.ticketsEgreso);
   const { user } = useAuth();

  const [selectedCliente, setSelectedCliente] = useState(data.cliente || null);

  const [clientesData, setClientesData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingClientes, setLoadingClientes] = useState(false);

  const [openDialogClientes, setOpenDialogClientes] = useState(false);

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const submitRef = React.useRef(false);

  const rowsPerPage = 5;
  const cacheRef = React.useRef({});

   const [valorPagado, setValorPagado] = useState(
     valorModificableParaVer == null ? '' : formatApiMoneyForInput(valorModificableParaVer)
   );
   const [selectedObservaciones, setSelectedObservaciones] = useState('Ninguna');

   useEffect(() => {
     setValorPagado(
       valorModificableParaVer == null ? '' : formatApiMoneyForInput(valorModificableParaVer)
     );
   }, [valorModificableParaVer]);

  useEffect(() => {
    if (data.cliente) {
      setSelectedCliente(data.cliente);
    }
  }, [data]);

  useEffect(() => {
    if (!openDialogClientes) return;

    let isMounted = true;

    const fetchClientes = async () => {
      const key = `${searchText}|${currentPage}`;
      const cached = cacheRef.current[key];

      if (cached) {
        setClientesData(cached.results);
        setTotalPages(Math.ceil(cached.count / rowsPerPage));
        return;
      }

      setLoadingClientes(true);

      try {
        const res = await axios.get(`${API_URL}/clientes/`, {
          params: {
            search: searchText,
            page: currentPage,
            page_size: rowsPerPage,
          },
        });

        if (!isMounted) return;

        const results = res.data.results || [];
        const count = res.data.count || 0;

        cacheRef.current[key] = { results, count };

        setClientesData(results);
        setTotalPages(Math.ceil(count / rowsPerPage));

      } catch (err) {
        console.error("Error cargando clientes:", err);
      } finally {
        if (isMounted) setLoadingClientes(false);
      }
    };

    fetchClientes();

    return () => {
      isMounted = false;
    };
  }, [searchText, currentPage, openDialogClientes]);

   if (!data?.historial?.placa) {
    console.error("Historial sin placa:", data.historial);
    return;
  }

   const handleCrearTicket = () => runTicketCreationOnce(submitRef, async () => {
    setLoadingSubmit(true);
    setSubmitError('');
    let pdfWindow = null;
    try {
      pdfWindow = openPdfPopup();
      if (!pdfWindow) {
        setSubmitError('Habilita las ventanas emergentes para imprimir el ticket.');
        return;
      }

      const firstTicket = data.ticketsEgreso.length === 0;
      const originalValue = data.salida.valor_pagado;
      const canonicalValue = parseColombianMoneyInput(valorPagado);
      const originalClienteId = data.cliente?.id || null;
      const originalObservaciones = data.observaciones || 'Ninguna';
      const fecha = data.salida.fecha_salida;
      const hora = data.salida.hora_salida;
      const tiempo = data.salida.tiempo_transcurrido;
      const cambios =
        selectedCliente?.id !== originalClienteId ||
        selectedObservaciones !== originalObservaciones ||
        canonicalValue !== normalizeApiMoney(originalValue);
      const payload = {
        ...buildTicketCreationContext(
          data.id,
          cambios ? selectedCliente?.id : originalClienteId
        ),
        fecha_salida: fecha,
        hora_salida: hora,
        tiempo_transcurrido: tiempo,
        valor_a_pagar: cambios ? canonicalValue : normalizeApiMoney(originalValue),
        estado: firstTicket ? 'Vigente' : 'Modificado',
        observaciones: cambios ? selectedObservaciones : originalObservaciones,
      };
      const requestConfig = await authenticatedSessionRequestConfig(API_URL, axios.get);
      const ticketVigente = ticketsEgresos.find((ticket) => ticket.estado === 'Vigente');
      if (ticketVigente &&
          (ticketVigente.fecha_salida !== fecha ||
            ticketVigente.hora_salida !== hora ||
            ticketVigente.valor_a_pagar !== originalValue ||
            ticketVigente.tiempo_transcurrido !== tiempo)) {
        await axios.put(
          `${API_URL}/tickets-salida/${ticketVigente.id}/`,
          { estado: 'Modificado' },
          requestConfig
        );
      }

      const createdTicket = await createTicketSnapshot({
        apiUrl: API_URL,
        tipo: 'salida',
        payload,
        requestConfig,
        httpClient: axios,
      });
      const pdfResponse = await axios.get(
        `${API_URL}/tickets-salida/${createdTicket.id}/`,
        { responseType: 'blob', withCredentials: true }
      );
      handleUpdateData({
        ...data,
        ticketsEgreso: appendCreatedTicket(data.ticketsEgreso, createdTicket),
      });
      if (canonicalValue !== normalizeApiMoney(originalValue)) {
        if (data.movimiento) {
          await updateMovimientoMonto(data.movimiento, canonicalValue);
        } else {
          await axios.put(`${API_URL}/salidas/${data.salida.id}/`, { valor_pagado: canonicalValue });
        }
        setValorModificableParaVer(canonicalValue);
      }
      openPdfBlob(pdfResponse.data, pdfWindow);
      setOpenTicketSalidaForm(false);
    } catch (error) {
      setSubmitError(getTicketCreationErrorMessage(error));
      console.error('No fue posible crear el ticket de salida:', error?.response?.status || error?.message);
      closePdfPopup(pdfWindow);
    } finally {
      setLoadingSubmit(false);
    }
  });

  return (
      <Dialog open={openTicketSalidaForm} onClose={() => setOpenTicketSalidaForm(false)} maxWidth="md" fullWidth>
        <DialogTitle>Ticket de Salida</DialogTitle>
        <DialogContent>
           <Typography variant="h6" gutterBottom sx={{ paddingTop: 2 }}>
             <strong>Empleado:</strong> {user.nombre}
           </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Fecha de Salida:</strong> {data.salida.fecha_salida}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Hora de Salida:</strong> {data.salida.hora_salida}
          </Typography>

          <InputLabel sx={{ paddingTop: 2 }}>Cliente</InputLabel>

          <TextField
            fullWidth
            value={
              selectedCliente
                ? `${selectedCliente.nombre} ${selectedCliente.apellidos}`
                : ''
            }
            disabled
          />

          <Button
            variant="outlined"
            sx={{ mt: 1 }}
            onClick={() => setOpenDialogClientes(true)}
            disabled={!areUserAdmin}
          >
            Buscar Cliente
          </Button>

          <TextField
            label="Valor a Pagar"
            variant="outlined"
            type="text"
            inputMode="decimal"
            fullWidth
            margin="normal"
            value={valorPagado ?? 0}
            onChange={(e) => setValorPagado(e.target.value)}
            disabled={!areUserAdmin}
          />
          {!areUserAdmin && data.ticketsEgreso.length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block">
              Solo los administradores pueden modificar este ticket.
            </Typography>
          )}

          <InputLabel sx={{ paddingTop: 2 }}>Observaciones</InputLabel>
          <TextField
            autoFocus
            fullWidth
            value={selectedObservaciones}
            onChange={(e) => setSelectedObservaciones(e.target.value)}
            inputProps={{ maxLength: 200 }}
            sx={{ textTransform: 'uppercase' }}
          />

{submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}

<Button
  variant="contained"
  color="primary"
  onClick={handleCrearTicket}
  sx={{ marginTop: 2 }}
  disabled={loadingSubmit}
>
  {loadingSubmit ? "Procesando..." : "Imprimir Ticket de Salida"}
</Button>
        </DialogContent>

        <Dialog
          open={openDialogClientes}
          onClose={() => setOpenDialogClientes(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Buscar Cliente</DialogTitle>

          <DialogContent>

            <TextField
              fullWidth
              placeholder="Buscar cliente..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
              sx={{ mb: 2 }}
            />

            {loadingClientes ? (
              <Typography>Cargando...</Typography>
            ) : (
              <>
                {clientesData.map((cliente) => (
                  <div
                    key={cliente.id}
                    style={{
                      padding: "10px",
                      borderBottom: "1px solid #ccc",
                      cursor: "pointer"
                    }}
                    onClick={() => {
                      setSelectedCliente(cliente);
                      setOpenDialogClientes(false);
                    }}
                  >
                    <strong>
                      {cliente.nombre} {cliente.apellidos}
                    </strong>
                    <br />
                    {cliente.tipo_documento}: {cliente.documento}
                  </div>
                ))}
              </>
            )}

            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
                <Button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  Anterior
                </Button>

                <Typography sx={{ mx: 2 }}>
                  {currentPage} / {totalPages}
                </Typography>

                <Button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  Siguiente
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </Dialog>
  );
}

export default TicketSalidaForm;
